/**
 * InlineOptimizeWidget — Renders optimization progress INLINE within a chat message.
 *
 * Shows:
 * - 3-step stepper (Baseline → Optimizing → Ready)
 * - Baseline ATS scores
 * - Per-section progress cards
 * - Final result with actions
 *
 * This replaces the separate canvas panel approach — everything is in the chat.
 */

import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Mail,
  Save,
  Search,
  Sparkles,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import LoadingSpinner from '../../../components/LoadingSpinner';

// ── Types ─────────────────────────────────────────────────────────────────────

export type OptimizePhase = 'baseline' | 'optimizing' | 'done' | 'error';

export type SectionStatus = 'pending' | 'loading' | 'done' | 'error' | 'skipped';

export interface SectionState {
  title: string;
  status: SectionStatus;
  originalContent?: string;
  optimizedContent?: string;
}

export interface BaselineData {
  overall: number;
  formatting: number;
  keywords: number;
  structure: number;
  atsRating: 'Excellent' | 'Good' | 'Fair' | 'Poor';
  matchScore: number;
  strengths: string[];
  warnings: string[];
}

export interface FinalResult {
  matchScoreBefore: number;
  matchScoreAfter: number;
  matchedSkills: string[];
  missingSkills: string[];
  improvements: string[];
  optimizedResume: string;
}

export interface InlineOptimizeWidgetProps {
  jobTitle: string;
  company: string;
  phase: OptimizePhase;
  baseline?: BaselineData | null;
  sections?: SectionState[];
  result?: FinalResult | null;
  error?: string | null;
  onSave?: () => void;
  onDownload?: (format: 'pdf' | 'docx' | 'txt') => void;
  onApply?: () => void;
  onCoverLetter?: () => void;
  onSearchSimilar?: () => void;
  saving?: boolean;
  saved?: boolean;
  applied?: boolean;
}

// ── Phase stepper ─────────────────────────────────────────────────────────────

const PHASES: { key: OptimizePhase; label: string }[] = [
  { key: 'baseline', label: 'Analyzing' },
  { key: 'optimizing', label: 'Optimizing' },
  { key: 'done', label: 'Ready' },
];

const PHASE_ORDER: OptimizePhase[] = ['baseline', 'optimizing', 'done'];

function PhaseStepper({ current }: { current: OptimizePhase }) {
  const currentIdx = current === 'error' ? -1 : PHASE_ORDER.indexOf(current);
  
  return (
    <div className="flex items-center gap-2 mb-4">
      {PHASES.map((step, i) => {
        const isComplete = currentIdx > i;
        const isActive = currentIdx === i;
        const isPending = currentIdx < i;
        
        return (
          <React.Fragment key={step.key}>
            {i > 0 && (
              <div
                className="flex-1 h-0.5 rounded-full transition-colors"
                style={{
                  background: isComplete
                    ? 'var(--ds-brand)'
                    : 'var(--ds-border)',
                }}
              />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center t-tag font-medium transition-colors"
                style={{
                  background: (isComplete || isActive) ? 'var(--ds-brand)' : 'var(--ds-bg-tinted)',
                  color:      (isComplete || isActive) ? '#fff' : 'var(--ds-text-muted)',
                }}
              >
                {isComplete ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <span
                className="t-tag font-medium"
                style={{ color: isActive ? 'var(--ds-brand)' : isPending ? 'var(--ds-text-muted)' : 'var(--ds-text-body)' }}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Baseline card ─────────────────────────────────────────────────────────────

function BaselineCard({ baseline }: { baseline: BaselineData }) {
  const ratingColor =
    baseline.atsRating === 'Excellent' ? '#22c55e' :
    baseline.atsRating === 'Good' ? '#84cc16' :
    baseline.atsRating === 'Fair' ? '#f59e0b' : '#ef4444';

  return (
    <div
      className="ds-card-sm mb-3"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="t-meta font-medium">
          Current Match Score
        </span>
        <span
          className="t-tag font-medium px-1.5 py-0.5 rounded"
          style={{ background: `${ratingColor}20`, color: ratingColor }}
        >
          {baseline.atsRating}
        </span>
      </div>
      
      <div className="text-[24px] font-bold mb-3" style={{ color: ratingColor }}>
        {baseline.overall}%
      </div>
      
      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        {[
          { label: 'Format', value: baseline.formatting },
          { label: 'Keywords', value: baseline.keywords },
          { label: 'Structure', value: baseline.structure },
        ].map((item) => (
          <div key={item.label} className="rounded-md py-1.5" style={{ background: 'var(--ds-bg-tinted)' }}>
            <div className="t-card-title t-num">
              {item.value}%
            </div>
            <div className="t-tag t-muted">
              {item.label}
            </div>
          </div>
        ))}
      </div>

      {baseline.warnings.length > 0 && (
        <div className="t-tag t-muted">
          <span className="font-medium">To improve:</span> {baseline.warnings.slice(0, 2).join(', ')}
        </div>
      )}
    </div>
  );
}

// ── Section progress cards ────────────────────────────────────────────────────

function SectionProgressCards({ sections }: { sections: SectionState[] }) {
  const completed = sections.filter(s => s.status === 'done' || s.status === 'skipped').length;
  const total = sections.length;

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-2">
        <span className="t-meta font-medium">
          Optimizing sections
        </span>
        <span className="t-tag t-muted">
          {completed}/{total}
        </span>
      </div>
      
      <div className="flex flex-wrap gap-1.5">
        {sections.map((sec, secIdx) => {
          const isLoading = sec.status === 'loading';
          const isDone = sec.status === 'done' || sec.status === 'skipped';
          const isError = sec.status === 'error';

          return (
            <div
              key={`${sec.title}-${secIdx}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md t-tag"
              style={{
                background: isDone
                  ? 'var(--ds-ok-bg)'
                  : isError
                  ? 'var(--ds-danger-bg)'
                  : isLoading
                  ? 'var(--ds-brand-soft)'
                  : 'var(--ds-bg-tinted)',
                color: isDone
                  ? 'var(--ds-ok-text)'
                  : isError
                  ? 'var(--ds-danger-text)'
                  : isLoading
                  ? 'var(--ds-brand)'
                  : 'var(--ds-text-muted)',
              }}
            >
              {isLoading && <LoadingSpinner size="xs" showText={false} />}
              {isDone && <CheckCircle2 className="w-3 h-3" />}
              {isError && <XCircle className="w-3 h-3" />}
              <span>{sec.title}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Final result card ─────────────────────────────────────────────────────────

function ResultCard({
  result,
  onSave,
  onDownload,
  onApply,
  onCoverLetter,
  onSearchSimilar,
  saving,
  saved,
  applied,
}: {
  result: FinalResult;
  onSave?: () => void;
  onDownload?: (format: 'pdf' | 'docx' | 'txt') => void;
  onApply?: () => void;
  onCoverLetter?: () => void;
  onSearchSimilar?: () => void;
  saving?: boolean;
  saved?: boolean;
  applied?: boolean;
}) {
  const [showPreview, setShowPreview] = useState(false);
  const improvement = result.matchScoreAfter - result.matchScoreBefore;

  return (
    <div
      className="ds-card-sm"
    >
      {/* Score improvement */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-baseline gap-1">
          <span className="t-card-title t-num t-muted">
            {result.matchScoreBefore}%
          </span>
          <span className="t-meta t-muted">→</span>
          <span className="t-hero t-num" style={{ color: 'var(--ds-ok)' }}>
            {result.matchScoreAfter}%
          </span>
        </div>
        <span
          className="t-tag font-medium px-1.5 py-0.5 rounded"
          style={{ background: 'var(--ds-ok-bg)', color: 'var(--ds-ok-text)' }}
        >
          +{improvement} pts
        </span>
      </div>

      {/* Skills */}
      {result.matchedSkills.length > 0 && (
        <div className="mb-2">
          <span className="t-tag font-medium t-body">
            Skills matched:{' '}
          </span>
          <span className="t-tag t-muted">
            {result.matchedSkills.slice(0, 5).join(', ')}
          </span>
        </div>
      )}

      {result.missingSkills.length > 0 && (
        <div className="mb-3">
          <span className="t-tag font-medium t-body">
            Consider adding:{' '}
          </span>
          <span className="t-tag t-warn">
            {result.missingSkills.slice(0, 4).join(', ')}
          </span>
        </div>
      )}

      {/* Preview toggle */}
      <button
        type="button"
        onClick={() => setShowPreview(!showPreview)}
        className="w-full flex items-center justify-between py-2 px-3 rounded-md mb-3 t-meta transition-colors"
        style={{ background: 'var(--ds-bg-tinted)' }}
      >
        <span className="flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" />
          Preview optimized resume
        </span>
        {showPreview ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {showPreview && (
        <div
          className="mb-3 p-3 rounded-md leading-relaxed max-h-[200px] overflow-y-auto prose prose-sm prose-slate dark:prose-invert max-w-none"
          style={{ background: 'var(--ds-bg-card)' }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {result.optimizedResume.slice(0, 2000) + (result.optimizedResume.length > 2000 ? '\n\n...' : '')}
          </ReactMarkdown>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {onSave && (
          <button
            type="button"
            onClick={onSave}
            disabled={saving || saved}
            className={`ds-btn ds-btn-sm disabled:opacity-50 ${saved ? '' : 'ds-btn-primary'}`}
            style={saved ? { background: 'var(--ds-ok-bg)', color: 'var(--ds-ok-text)' } : {}}
          >
            {saving ? <LoadingSpinner size="xs" showText={false} /> : saved ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
            {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
          </button>
        )}

        {onDownload && (
          <button
            type="button"
            onClick={() => onDownload('pdf')}
            className="ds-btn ds-btn-secondary ds-btn-sm"
          >
            <Download className="w-3 h-3" />
            PDF
          </button>
        )}

        {onCoverLetter && (
          <button
            type="button"
            onClick={onCoverLetter}
            className="ds-btn ds-btn-secondary ds-btn-sm"
          >
            <Mail className="w-3 h-3" />
            Cover Letter
          </button>
        )}

        {onApply && (
          <button
            type="button"
            onClick={onApply}
            disabled={applied}
            className="ds-btn ds-btn-sm disabled:opacity-50"
            style={applied
              ? { background: 'var(--ds-ok-bg)', color: 'var(--ds-ok-text)' }
              : { background: 'var(--ds-ok)', color: '#fff' }}
          >
            <Sparkles className="w-3 h-3" />
            {applied ? 'Applied' : 'Apply'}
          </button>
        )}

        {onSearchSimilar && (
          <button
            type="button"
            onClick={onSearchSimilar}
            className="ds-btn ds-btn-secondary ds-btn-sm"
          >
            <Search className="w-3 h-3" />
            Similar Jobs
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function InlineOptimizeWidget({
  jobTitle,
  company,
  phase,
  baseline,
  sections,
  result,
  error,
  onSave,
  onDownload,
  onApply,
  onCoverLetter,
  onSearchSimilar,
  saving,
  saved,
  applied,
}: InlineOptimizeWidgetProps) {
  if (phase === 'error' && error) {
    return (
      <div
        className="ds-recessed ds-recessed-danger mt-3 rounded-lg p-3"
      >
        <div className="flex items-center gap-2 t-meta">
          <XCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div       className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--ds-border)' }}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4" style={{ color: 'var(--ds-brand)' }} />
        <span className="t-meta font-medium">
          Resume Optimization
        </span>
        <span className="t-tag t-muted">
          for {jobTitle} at {company}
        </span>
      </div>

      {/* Phase stepper */}
      <PhaseStepper current={phase} />

      {/* Baseline phase content */}
      {phase === 'baseline' && (
        <div className="flex items-center gap-2 py-4 justify-center">
          <LoadingSpinner size="sm" showText={false} />
          <span className="t-body t-muted">
            Analyzing your resume against job requirements...
          </span>
        </div>
      )}

      {/* Optimizing phase content */}
      {phase === 'optimizing' && (
        <>
          {baseline && <BaselineCard baseline={baseline} />}
          {sections && sections.length > 0 && <SectionProgressCards sections={sections} />}
        </>
      )}

      {/* Done phase content */}
      {phase === 'done' && result && (
        <ResultCard
          result={result}
          onSave={onSave}
          onDownload={onDownload}
          onApply={onApply}
          onCoverLetter={onCoverLetter}
          onSearchSimilar={onSearchSimilar}
          saving={saving}
          saved={saved}
          applied={applied}
        />
      )}
    </div>
  );
}

export default InlineOptimizeWidget;
